import type { RenderPath, RenderSignal } from './knockContent';

/**
 * Cutover telemetry for the in-app notification feed (RSP-6347).
 *
 * While the feed migrates from app-side hydration to Knock-templated content, we emit one
 * analytics event per feed item the first time a user sees it, recording whether it
 * rendered from Knock or the legacy fallback, which workflow it was, and how old the item
 * is. The fallback-share trend (and its age profile) is what tells us when it is safe to
 * remove the app-side hydration in Stage 6 (RSP-6235) — including whether a ~3-month
 * feed-history cut-off is acceptable.
 *
 * This whole module is throwaway: it is deleted alongside the fallback machinery in
 * Stage 6.
 */

/** Segment/Amplitude event name for a single feed-item render. */
export const NOTIFICATION_FEED_ITEM_RENDERED_EVENT =
  'Notification Feed Item Rendered';

/**
 * Coarse age of a feed item at render time. The 30-90d / >90d split is the one that
 * answers "can we cut off feed history at ~3 months?". `unknown` when the item carried no
 * usable `inserted_at`.
 */
export type ItemAgeBucket =
  | '<1d'
  | '1-7d'
  | '7-30d'
  | '30-90d'
  | '>90d'
  | 'unknown';

const DAY_MS = 86_400_000;

/**
 * Bucket a feed item's age. `now` is injected (not read from the clock) so the result is
 * deterministic and testable. Items with a future `inserted_at` (clock skew) bucket as the
 * youngest band rather than `unknown`.
 */
export const toItemAgeBucket = (
  insertedAt: string | null,
  now: number
): ItemAgeBucket => {
  if (!insertedAt) {
    return 'unknown';
  }

  const inserted = Date.parse(insertedAt);
  if (Number.isNaN(inserted)) {
    return 'unknown';
  }

  const ageDays = (now - inserted) / DAY_MS;
  if (ageDays < 1) {
    return '<1d';
  }
  if (ageDays < 7) {
    return '1-7d';
  }
  if (ageDays < 30) {
    return '7-30d';
  }
  if (ageDays < 90) {
    return '30-90d';
  }

  return '>90d';
};

/**
 * Wire properties for a render event. Bounded cardinality only — no message text, entity
 * titles, or user/org/item identifiers (`workflowKey` is one of ~31 known values, the
 * buckets are fixed). snake_case keys keep the Amplitude event-property names consistent.
 */
export type NotificationRenderEventProperties = {
  render_path: RenderPath;
  workflow_key: string | null;
  item_age_bucket: ItemAgeBucket;
  knock_has_link: boolean;
  handler_exists: boolean;
};

export const buildRenderEventProperties = (
  signal: RenderSignal,
  now: number
): NotificationRenderEventProperties => ({
  render_path: signal.renderPath,
  workflow_key: signal.workflowKey,
  item_age_bucket: toItemAgeBucket(signal.insertedAt, now),
  knock_has_link: signal.knockHasLink,
  handler_exists: signal.handlerExists,
});
